/**
 * teamManagementData.js
 * Seed data + pure helper functions ported 1:1 from the original page's
 * inline <script> block (avatarColours, users, teams, initials(),
 * colourForName(), formatDate(), formatLoginShort()).
 *
 * Swap `initialUsers` / `initialTeams` for real API calls when wiring this
 * page up (e.g. getOrgUsers() / getOrgTeams()) — the shape below matches
 * exactly what the original static arrays contained, so no consumer code
 * needs to change.
 */

export const AVATAR_COLOURS = [
  "#1d4ed8",
  "#d97706",
  "#16a34a",
  "#7c3aed",
  "#0891b2",
  "#dc2626",
  "#0d1b3e",
];

export const TEAM_COLOURS = [
  "#1d4ed8",
  "#d97706",
  "#16a34a",
  "#dc2626",
  "#7c3aed",
  "#0891b2",
];

export const ROLE_OPTIONS = ["Member", "Viewer"];

export const initialUsers = [
  {
    id: 1,
    name: "Henry Dimobika",
    email: "henry@mainsource.org",
    role: "Admin",
    team: "N/A",
    lastLogin: "2022-06-10T09:14:00",
    status: "active",
  },
  {
    id: 2,
    name: "Clara Osei",
    email: "clara@mainsource.org",
    role: "Manager",
    team: "Engineering",
    lastLogin: "2024-11-02T14:30:00",
    status: "active",
  },
  {
    id: 3,
    name: "Kwame Boateng",
    email: "kwame@mainsource.org",
    role: "Member",
    team: "Design",
    lastLogin: null,
    status: "pending",
  },
  {
    id: 4,
    name: "Ama Asante",
    email: "ama@mainsource.org",
    role: "Member",
    team: "Engineering",
    lastLogin: null,
    status: "pending",
  },
];

export const initialTeams = [
  {
    id: 1,
    name: "Engineering",
    desc: "Backend and infrastructure",
    colour: "#1d4ed8",
    manager: "Clara Osei",
    members: [2, 4],
  },
  {
    id: 2,
    name: "Design",
    desc: "Product design and UI/UX",
    colour: "#7c3aed",
    manager: "Henry Dimobika",
    members: [3],
  },
];

// Team names available to assign users to / for the filter dropdown.
// In the original this was hardcoded in the <option> lists (Engineering, Design).
export const TEAM_NAMES = ["Engineering", "Design"];

// Candidates for the "Team Manager" select in Add/Edit Team modals —
// hardcoded in the original as two <option> entries.
export const MANAGER_CANDIDATES = ["Henry Dimobika", "Clara Osei"];

/** initials('Henry Dimobika') -> 'HD' */
export function getInitials(name) {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

/** Deterministic colour per name, ported exactly from colourForName(). */
export function colourForName(name) {
  let sum = 0;
  for (let i = 0; i < name.length; i++) sum += name.charCodeAt(i);
  return AVATAR_COLOURS[sum % AVATAR_COLOURS.length];
}

/**
 * Full date + time split, for the desktop table's two-line date cell.
 * Returns null when there's no login yet (caller renders the
 * "Never logged in" placeholder in that case) — mirrors formatDate(iso).
 */
export function formatDateFull(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const dateStr = `${days[d.getDay()]}, ${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
  let h = d.getHours();
  const m = d.getMinutes();
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  const timeStr = `${h}:${m < 10 ? "0" + m : m} ${ampm}`;
  return { dateStr, timeStr };
}

/** Short "Mon D, YYYY" form used in the mobile card list. Ported from formatLoginShort(). */
export function formatLoginShort(iso) {
  if (!iso) return "Never";
  const d = new Date(iso);
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

/** Role -> badge visual config (class suffix + icon name resolved in RoleBadge.jsx). */
export const ROLE_BADGE_CONFIG = {
  Admin: { key: "admin", icon: "crown" },
  Manager: { key: "manager", icon: "userStar" },
  Member: { key: "member", icon: "userCheck" },
  Viewer: { key: "viewer", icon: "eye" },
};
